import { useEffect, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { useApi } from '@fortem-monorepo/react-spa'
import {
  ControlledNumberInput,
  DatePicker,
  FormField,
  FormItem,
  FormLabel,
  Input,
  SearchSelect,
  Textarea,
} from '@fortem-monorepo/react-ui'

import { SchemaType } from './AddSecurityEntryForm.schema'

export interface AddSecurityEntryFormFieldsProps {}

export const AddSecurityEntryFormFields: React.FC<AddSecurityEntryFormFieldsProps> = ({}) => {
  const form = useFormContext<SchemaType>()
  const { control, register, setValue, trigger } = form

  const api = useApi()

  useEffect(() => {
    trigger()
  }, [])

  const instrumentsQuery = useQuery({
    queryKey: ['smartrade', 'data', 'isins'],
    queryFn: () => api.smartrade.getIsins(),
  })

  const options = useMemo(() => {
    return (instrumentsQuery.data ?? [])
      .map((i) => {
        return {
          label: i.isin,
          value: i.isin,
        }
      })
      .filter((i) => !!i.value)
  }, [instrumentsQuery.data])

  return (
    <div className={'flex flex-col gap-4'}>
      <FormField
        name="isin"
        control={control}
        render={({ field: { value } }) => {
          return (
            <FormItem>
              <FormLabel>ISIN</FormLabel>
              <SearchSelect
                placeholder={'Select an instrument'}
                loading={instrumentsQuery.isFetching}
                options={options}
                value={value ?? ''}
                setValue={(val) => {
                  setValue('isin', val, { shouldValidate: true })
                }}
              />
            </FormItem>
          )
        }}
      />

      <FormField
        name="start_date"
        control={control}
        render={({ field: { value, onChange, ...fieldProps } }) => {
          return (
            <FormItem>
              <FormLabel>Start Date</FormLabel>
              <DatePicker
                selected={value ? new Date(value) : undefined}
                onSelect={(date) =>
                  setValue('start_date', date ? toISO8601(date) : '', { shouldValidate: true })
                }
                {...fieldProps}
              />
            </FormItem>
          )
        }}
      />
      <FormField
        name="full_fee_date"
        control={control}
        render={({ field: { value, onChange, ...fieldProps } }) => {
          return (
            <FormItem>
              <FormLabel>Full Fee Date</FormLabel>
              <DatePicker
                selected={value ? new Date(value) : undefined}
                onSelect={(date) =>
                  setValue('full_fee_date', date ? toISO8601(date) : '', { shouldValidate: true })
                }
                {...fieldProps}
              />
            </FormItem>
          )
        }}
      />
      <FormField
        name="start_drip_date"
        control={control}
        render={({ field: { value, onChange, ...fieldProps } }) => {
          return (
            <FormItem>
              <FormLabel>Start Drip Date</FormLabel>
              <DatePicker
                selected={value ? new Date(value) : undefined}
                onSelect={(date) =>
                  setValue('start_drip_date', date ? toISO8601(date) : '', { shouldValidate: true })
                }
                {...fieldProps}
              />
            </FormItem>
          )
        }}
      />
      <FormField
        name="end_drip_date"
        control={control}
        render={({ field: { value, onChange, ...fieldProps } }) => {
          return (
            <FormItem>
              <FormLabel>End Drip Date</FormLabel>
              <DatePicker
                selected={value ? new Date(value) : undefined}
                onSelect={(date) =>
                  setValue('end_drip_date', date ? toISO8601(date) : '', { shouldValidate: true })
                }
                {...fieldProps}
              />
            </FormItem>
          )
        }}
      />

      <FormField
        name="start_fee_bps"
        control={control}
        render={() => {
          return (
            <FormItem>
              <FormLabel>Start Drip (bps)</FormLabel>
              <ControlledNumberInput thousandSeparator={','} {...register('start_fee_bps')} />
            </FormItem>
          )
        }}
      />
      <FormField
        name="full_fee_bps"
        control={control}
        render={() => {
          return (
            <FormItem>
              <FormLabel>Full Drip (bps)</FormLabel>
              <ControlledNumberInput thousandSeparator={','} {...register('full_fee_bps')} />
            </FormItem>
          )
        }}
      />
      <FormField
        name="end_drip_bps"
        control={control}
        render={() => {
          return (
            <FormItem>
              <FormLabel>End Drip (bps)</FormLabel>
              <ControlledNumberInput thousandSeparator={','} {...register('end_drip_bps')} />
            </FormItem>
          )
        }}
      />

      <FormField
        name="country"
        control={control}
        render={() => {
          return (
            <FormItem>
              <FormLabel>Country</FormLabel>
              <Input {...register('country')} />
            </FormItem>
          )
        }}
      />
      <FormField
        name="description"
        control={control}
        render={() => {
          return (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <Textarea {...register('description')} />
            </FormItem>
          )
        }}
      />
    </div>
  )
}
